/**
 * Listado de remitos ANMAT pendientes de confirmar (recepción).
 *
 * Datos REALES desde el backend, que pega a riga-traza-v2 → ANMAT
 * (`transacciones-no-confirmadas-agrupadas`). 1 card por remito.
 *
 * Sin precarga: hay miles de pendientes viejos en ANMAT y bajarlos todos es
 * lento. Se busca por remito o factura (puntual), o acotando por emisor,
 * producto, lote y rango de fecha de transacción (DEVS-4077); ANMAT resuelve
 * esos filtros del lado del WS. Cada card muestra el estado del acta local si
 * ya se abrió (`localTask`).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Loader2, Package, Search, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useRecepcionPendientes } from '@/api/queries';
import { listarProductos } from '@/api/endpoints/producto-acsis';
import type { RecepcionPendientesFilters } from '@/api/endpoints/recepcion-pendientes';
import { useSessionStore } from '@/stores/session';
import type { RecepcionRemito } from '@/api/contracts/recepcion-pendientes';
import { ESTADO_ACTA_LABEL, type EstadoActaRecepcion } from '@/domain/types';
import { BADGE_ESTADO_ACTA } from './acta-helpers';

const MAX_REMITOS_LIMITE = 500;

/**
 * Cantidad de remitos que se le pide a ANMAT en una búsqueda puntual
 * (remito/factura). Configurable por env (VITE_RECEPCION_MAX_REMITOS_DEFAULT);
 * si no está o es inválida, fallback a 3.
 */
function parseMaxRemitosDefault(): number {
  const raw = Number(import.meta.env.VITE_RECEPCION_MAX_REMITOS_DEFAULT);
  if (!Number.isInteger(raw) || raw < 1 || raw > MAX_REMITOS_LIMITE) return 3;
  return raw;
}
const MAX_REMITOS_PUNTUAL = parseMaxRemitosDefault();
/** Con filtros amplios (fecha, emisor, producto) se traen más remitos. */
const MAX_REMITOS_AMPLIO = 50;

type Campo = 'remito' | 'factura';

type Filtros = Omit<RecepcionPendientesFilters, 'maxRemitos'>;

interface PendientesRecepcionListProps {
  onAbrirRemito?: (remito: RecepcionRemito) => void;
  /** Mientras se abre un acta, las cards no responden al clic. */
  abriendo?: boolean;
}

export function PendientesRecepcionList({
  onAbrirRemito,
  abriendo = false,
}: PendientesRecepcionListProps = {}) {
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);

  // Lo que el usuario tipea vs lo que dispara la query (recién al buscar).
  const [campo, setCampo] = useState<Campo>('remito');
  const [texto, setTexto] = useState('');
  const [masFiltros, setMasFiltros] = useState(false);
  const [glnOrigen, setGlnOrigen] = useState('');
  const [producto, setProducto] = useState('');
  const [gtinElegido, setGtinElegido] = useState('');
  const [lote, setLote] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [aplicado, setAplicado] = useState<Filtros>({});
  const [habilitado, setHabilitado] = useState(false);

  const esPuntual = Boolean(aplicado.remito || aplicado.factura);
  const { data, isLoading, error, isFetching } = useRecepcionPendientes(
    ubicacionActivaId,
    esPuntual ? MAX_REMITOS_PUNTUAL : MAX_REMITOS_AMPLIO,
    aplicado,
    habilitado,
  );

  // Producto por nombre → GTIN del maestro local (ANMAT filtra por GTIN).
  const nombreBusqueda = producto.trim();
  const esGtin = /^\d{8,14}$/.test(nombreBusqueda);
  const productos = useQuery({
    queryKey: ['productos-listar', 'recepcion', nombreBusqueda],
    queryFn: () => listarProductos({ nombre: nombreBusqueda }),
    enabled: masFiltros && !esGtin && nombreBusqueda.length >= 3,
    staleTime: 5 * 60_000,
  });
  const opcionesProducto = (productos.data?.items ?? []).filter((p) => p.codigoDeBarras);

  const remitos = data?.remitos ?? [];
  const hayBusqueda = habilitado;

  const buscar = (e: React.FormEvent) => {
    e.preventDefault();
    const valor = texto.trim();
    const gtin = esGtin ? nombreBusqueda : gtinElegido || undefined;
    const f: Filtros = {
      ...(valor ? { [campo]: valor } : {}),
      glnOrigen: glnOrigen.trim() || undefined,
      gtin,
      lote: lote.trim() || undefined,
      fechaDesde: fechaDesde || undefined,
      fechaHasta: fechaHasta || undefined,
    };
    const hayAlgo = Boolean(
      valor || f.glnOrigen || f.gtin || f.lote || f.fechaDesde || f.fechaHasta,
    );
    // Sin ningún criterio no se dispara nada: no mostramos "todos los pendientes".
    if (!hayAlgo) return;
    setAplicado(f);
    setHabilitado(true);
  };

  const limpiar = () => {
    setTexto('');
    setGlnOrigen('');
    setProducto('');
    setGtinElegido('');
    setLote('');
    setFechaDesde('');
    setFechaHasta('');
    setAplicado({});
    setHabilitado(false);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4">
          <form onSubmit={buscar} className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex overflow-hidden rounded-md border border-gray-200">
                {(['remito', 'factura'] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCampo(c)}
                    className={cn(
                      'px-3 py-1.5 text-xs capitalize transition',
                      campo === c
                        ? 'bg-everex-azul font-semibold text-white'
                        : 'bg-white text-gray-600 hover:text-everex-azul',
                    )}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="relative min-w-[200px] flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder={`Buscar por n° de ${campo}`}
                  className="pl-9 font-mono"
                />
              </div>
              <Button
                type="submit"
                disabled={isFetching}
                className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
              >
                {isFetching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="ml-1.5">Buscar</span>
              </Button>
              {hayBusqueda && (
                <Button type="button" variant="outline" onClick={limpiar} title="Limpiar búsqueda">
                  <X className="h-4 w-4" />
                </Button>
              )}
              <button
                type="button"
                onClick={() => setMasFiltros((v) => !v)}
                className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-everex-azul"
              >
                {masFiltros ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                Más filtros
              </button>
            </div>

            {masFiltros && (
              <div className="grid grid-cols-1 gap-2 border-t border-gray-100 pt-2 md:grid-cols-3 lg:grid-cols-6">
                <div>
                  <Label className="mb-1 block text-xs">Fecha desde</Label>
                  <Input
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Fecha hasta</Label>
                  <Input
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">GLN emisor</Label>
                  <Input
                    value={glnOrigen}
                    onChange={(e) => setGlnOrigen(e.target.value)}
                    placeholder="13 dígitos"
                    className="font-mono"
                  />
                </div>
                <div className="lg:col-span-2">
                  <Label className="mb-1 block text-xs">Producto (nombre o GTIN)</Label>
                  <Input
                    value={producto}
                    onChange={(e) => {
                      setProducto(e.target.value);
                      setGtinElegido('');
                    }}
                    placeholder="Ej.: Mizarid o 07798113702357"
                  />
                  {!esGtin && nombreBusqueda.length >= 3 && (
                    <div className="mt-1">
                      {productos.isFetching ? (
                        <span className="text-[11px] text-gray-500">Buscando en el maestro…</span>
                      ) : opcionesProducto.length === 0 ? (
                        <span className="text-[11px] text-gray-500">
                          Sin coincidencias en el maestro.
                        </span>
                      ) : (
                        <Select value={gtinElegido} onValueChange={setGtinElegido}>
                          <SelectTrigger>
                            <SelectValue placeholder="Elegí el artículo" />
                          </SelectTrigger>
                          <SelectContent>
                            {opcionesProducto.map((p) => (
                              <SelectItem
                                key={`${p.sku}-${p.codigoDeBarras}`}
                                value={p.codigoDeBarras ?? ''}
                              >
                                {p.nombre ?? p.sku} · {p.codigoDeBarras}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Lote</Label>
                  <Input
                    value={lote}
                    onChange={(e) => setLote(e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {!habilitado ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <Package className="h-6 w-6 text-gray-500" />
            </div>
            <div>
              <h3 className="font-semibold text-everex-azul">Buscá una recepción pendiente</h3>
              <p className="mt-1 text-xs text-gray-600">
                Por n° de remito o factura, o con "Más filtros" por fecha, emisor, producto o lote.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">
            No se pudieron obtener los pendientes:{' '}
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      ) : isLoading && !data ? (
        <div className="flex items-center justify-center py-12 text-gray-600">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Consultando ANMAT…
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-600">
            {remitos.length} remito{remitos.length === 1 ? '' : 's'} pendiente
            {remitos.length === 1 ? '' : 's'}
            {data && data.totalRemitos > data.remitosDevueltos
              ? ` · se muestran ${data.remitosDevueltos} de ${data.totalRemitos}: acotá la búsqueda`
              : ''}
          </p>
          {remitos.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <Package className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-everex-azul">Sin resultados</h3>
                <p className="mt-1 text-xs text-gray-600">
                  Ningún remito pendiente en ANMAT coincide con la búsqueda.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div
              className={cn(
                'grid grid-cols-1 gap-3 md:grid-cols-2',
                isFetching && 'opacity-60 transition-opacity',
              )}
            >
              {remitos.map((remito, i) => (
                <RemitoCard
                  key={`${remito.numeroRemito ?? remito.numeroFactura ?? '?'}-${remito.glnOrigen ?? ''}-${i}`}
                  remito={remito}
                  staggerIndex={i}
                  onClick={onAbrirRemito && !abriendo ? () => onAbrirRemito(remito) : undefined}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RemitoCard({
  remito,
  staggerIndex,
  onClick,
}: {
  remito: RecepcionRemito;
  staggerIndex?: number;
  onClick?: () => void;
}) {
  const dataStagger = staggerIndex !== undefined ? Math.min(staggerIndex, 11) : undefined;
  const clickable = Boolean(onClick);
  const local = remito.localTask ?? null;
  const conProblema = local?.estado === 'error';
  const evento = remito.series[0]?.descripcionEvento ?? null;

  return (
    <Card
      onClick={onClick}
      data-stagger={dataStagger}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        dataStagger !== undefined && 'stagger-item',
        clickable && 'cursor-pointer transition hover:shadow-md',
        conProblema
          ? 'border-red-300 bg-red-50 hover:border-red-500'
          : clickable && 'hover:border-everex-naranja',
      )}
    >
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="badge badge-purple">
                <b>RI</b> · Recepción ingreso
              </span>
              {remito.numeroRemito ? (
                <span className="text-sm font-bold text-everex-azul">{remito.numeroRemito}</span>
              ) : null}
            </div>
            {evento && <div className="mt-1 text-xs font-medium text-gray-700">{evento}</div>}
            <div className="mt-1 text-xs text-gray-600">
              Emisor: <b>{remito.razonSocialOrigen ?? '—'}</b>
              {remito.glnOrigen ? (
                <span className="font-mono text-gray-500"> · {remito.glnOrigen}</span>
              ) : null}
            </div>
            <div className="mt-1 text-xs text-gray-600">
              Factura:{' '}
              <span className="font-mono text-gray-800">{remito.numeroFactura ?? '—'}</span>
            </div>
          </div>
          <div className="text-right">
            <BadgeActa remito={remito} />
            <div className="mt-1 text-xs text-gray-600">
              {remito.cantSeries} serie{remito.cantSeries === 1 ? '' : 's'} pendiente
              {remito.cantSeries === 1 ? '' : 's'}
            </div>
            {local && local.totales.marcada ? (
              <div className="text-[11px] text-blue-800">
                {local.totales.marcada} marcada{local.totales.marcada === 1 ? '' : 's'}
              </div>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Estado del acta local si ya se abrió; si no, "Por recibir". */
function BadgeActa({ remito }: { remito: RecepcionRemito }) {
  const local = remito.localTask;
  if (!local) return <span className="badge badge-yellow">Por recibir</span>;
  const estado = local.estado as EstadoActaRecepcion;
  const label = ESTADO_ACTA_LABEL[estado] ?? local.estado;
  const clase = BADGE_ESTADO_ACTA[estado] ?? 'badge-gray';
  const titulo =
    local.totales.error > 0
      ? `${local.totales.error} de ${local.totales.total} series rechazadas por ANMAT`
      : local.totales.noRecibida
        ? `${local.totales.noRecibida} no recibida${local.totales.noRecibida === 1 ? '' : 's'}`
        : undefined;
  return (
    <span className={cn('badge', clase)} title={titulo}>
      {label}
    </span>
  );
}
